import { Fragment } from "react";
import { FieldRenderer } from "../../../../components/forms/FieldRenderer";
import type { FormFieldRef } from "../../../../components/forms/types";
import { isFieldInline } from "../../../../lib/forms/fieldOptions";
import type { FieldDto } from "../../../../types/field";
import type { SectionDto } from "../../../../types/section";

interface SectionFieldsProps {
  section: SectionDto;
  visibleByName: Record<string, boolean>;
  valuesByName?: Record<string, string>;
  onFieldValueChange: (fieldName: string, value: string) => void;
  registerRef: (field: FieldDto) => (instance: FormFieldRef) => void;
  introText?: string;
}

export function SectionFields({
  section,
  visibleByName,
  valuesByName = {},
  onFieldValueChange,
  registerRef,
  introText,
}: SectionFieldsProps) {
  const sortedFields = [...(section.fields ?? [])].sort(
    (a, b) => a.order - b.order,
  );

  const visibleFields = sortedFields.filter(
    (field) => visibleByName[field.name] !== false,
  );
  const inlineFields = visibleFields.filter((field) => isFieldInline(field));
  const stackedFields = visibleFields.filter((field) => !isFieldInline(field));

  return (
    <>
      {inlineFields.length > 0 ? (
        <p className="mb-2 font-onest text-base leading-relaxed text-ink">
          {introText ? <span>{introText} </span> : null}
          {inlineFields.map((field, index) => (
            <Fragment key={field.name}>
              {index > 0 ? "\u00a0" : null}
              <FieldRenderer
                field={field}
                layout="inline"
                visible
                valuesByName={valuesByName}
                inputRef={registerRef(field)}
                onFieldValueChange={onFieldValueChange}
              />
            </Fragment>
          ))}
        </p>
      ) : null}

      {stackedFields.length > 0 ? (
        <div className="grid grid-cols-1 items-end gap-x-4 gap-y-5 sm:grid-cols-2">
          {stackedFields.map((field) => (
            <FieldRenderer
              key={field.name}
              field={field}
              layout="stacked"
              visible={visibleByName[field.name] !== false}
              valuesByName={valuesByName}
              inputRef={registerRef(field)}
              onFieldValueChange={onFieldValueChange}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}
